import {
  ProtocolMessageType,
  RECOVERY_CHANNEL_NAME,
  RuntimeStage,
  createAppInstallMessage,
  createAppUninstallMessage,
  createInitChannelMessage,
  createCloseChannelMessage,
  isControlMessage,
} from '../../../protocol/src/messages.js'
import { validateAppId } from '../../../protocol/src/app-catalog.js'
import { getOrCreateInstanceSession, selectInstanceSession } from './session.js'
import { runtimeEntryUrl } from './runtime-version.js'
import { ServiceWorkerCoordinator } from './sw-coordinator.js'

export const PlaygroundEventType = Object.freeze({
  PROGRESS: 'progress',
  READY: 'ready',
  ERROR: 'error',
  WAKING_UP: 'waking_up',
  WOKE_UP: 'woke_up',
})

export class PlaygroundController {
  constructor(options = {}) {
    const appOperationTimeoutMs = options.appOperationTimeoutMs
      ?? options.appInstallTimeoutMs
      ?? 600000
    this.options = {
      serviceWorkerUrl: runtimeEntryUrl('/sw.js'),
      serverWorkerUrl: runtimeEntryUrl('/worker.js'),
      recoveryChannelName: RECOVERY_CHANNEL_NAME,
      serviceWorkerRegistrationTimeoutMs: 15000,
      serviceWorkerUpgradeTimeoutMs: 30000,
      readyDelayMs: 2000,
      appOperationTimeoutMs,
      ...options,
    }
    this.environment = {
      navigator: options.navigator || globalThis.navigator,
      document: options.document || globalThis.document,
      location: options.location || globalThis.location,
      WorkerClass: options.WorkerClass || globalThis.Worker,
      MessageChannelClass: options.MessageChannelClass || globalThis.MessageChannel,
      BroadcastChannelClass: options.BroadcastChannelClass || globalThis.BroadcastChannel,
      setTimeoutFn: options.setTimeoutFn || ((...args) => globalThis.setTimeout(...args)),
      clearTimeoutFn: options.clearTimeoutFn || (timer => globalThis.clearTimeout(timer)),
      registrationSetTimeoutFn: options.registrationSetTimeoutFn
        || ((...args) => globalThis.setTimeout(...args)),
      registrationClearTimeoutFn: options.registrationClearTimeoutFn
        || (timer => globalThis.clearTimeout(timer)),
    }
    this.swCoordinator = new ServiceWorkerCoordinator({
      serviceWorkerUrl: this.options.serviceWorkerUrl,
      serviceWorkerRegistrationTimeoutMs: this.options.serviceWorkerRegistrationTimeoutMs,
      serviceWorkerUpgradeTimeoutMs: this.options.serviceWorkerUpgradeTimeoutMs,
      location: this.environment.location,
      setTimeoutFn: this.environment.setTimeoutFn,
      clearTimeoutFn: this.environment.clearTimeoutFn,
      registrationSetTimeoutFn: this.environment.registrationSetTimeoutFn,
      registrationClearTimeoutFn: this.environment.registrationClearTimeoutFn,
    })
    this.listeners = new Map()
    this.session = null
    this.worker = null
    this.recoveryChannel = null
    this.readyTimer = 0
    this.runtimeReady = false
    this.installedApps = []
    this.disposed = false
    this.handleControllerChange = null
    this.handleVisibilityChange = null
    this.pendingAppOperations = new Map()
  }

  on(type, listener) {
    const listeners = this.listeners.get(type) || new Set()
    listeners.add(listener)
    this.listeners.set(type, listeners)
    return () => listeners.delete(listener)
  }

  emit(type, detail) {
    for (const listener of this.listeners.get(type) || []) listener(detail)
  }

  progress(stage, status, message) {
    this.emit(PlaygroundEventType.PROGRESS, { stage, status, message })
  }

  start() {
    if (this.startPromise) return this.startPromise
    this.abortController = new AbortController()
    const starting = this.startRuntime(this.abortController.signal)
    this.startPromise = starting
    // Synchronous failures can dispose before the promise assignment above.
    starting.catch(() => {
      if (this.startPromise === starting) this.startPromise = null
    })
    return starting
  }

  async startRuntime(signal) {
    this.disposed = false
    this.reloadingForServiceWorkerUpdate = false

    try {
      const serviceWorker = this.environment.navigator?.serviceWorker
      if (!serviceWorker) throw new Error('Service workers are unavailable in this browser.')

      this.progress(RuntimeStage.SERVICE_WORKER, 'active', 'Starting service worker...')
      const sessionOptions = {
        storage: this.options.storage,
        now: this.options.now,
        random: this.options.random,
      }
      this.session = this.options.session || (this.options.instanceId
        ? selectInstanceSession(this.options.instanceId, sessionOptions)
        : getOrCreateInstanceSession(sessionOptions))
      if (!this.session) {
        throw new Error(`Playground instance not found: ${this.options.instanceId}`)
      }

      this.handleControllerChange = this.swCoordinator.setupControllerChangeReload(
        serviceWorker,
        () => {
          if (!this.disposed && !this.reloadingForServiceWorkerUpdate) {
            this.reloadingForServiceWorkerUpdate = true
            console.log('[Playground] Service Worker updated! Auto-reloading to apply changes...')
            this.environment.location.reload()
          }
        },
      )

      const registration = await this.withRegistrationTimeout(
        serviceWorker.register(this.options.serviceWorkerUrl, {
          type: 'module',
          updateViaCache: 'none',
        }),
      )
      signal.throwIfAborted()
      this.registration = registration
      this.serviceWorkerTarget = this.expectedServiceWorker(serviceWorker, registration)

      if (!this.serviceWorkerTarget) {
        const controllerReady = this.waitForExpectedServiceWorker(serviceWorker)
        this.progress(RuntimeStage.SERVICE_WORKER, 'active', 'Updating service worker...')
        const upgraded = await controllerReady
        signal.throwIfAborted()
        if (upgraded === false) {
          throw new Error('The service worker update did not activate. Reload the page to retry.')
        }
        this.serviceWorkerTarget = serviceWorker.controller
      }

      this.progress(RuntimeStage.SERVICE_WORKER, 'done', 'Service worker connected.')
      this.createWorker()
      this.setupChannel()
      this.setupRecovery()
      return this.session
    } catch (error) {
      if (!signal.aborted) {
        this.emitError(error)
        this.dispose()
      }
      throw error
    }
  }

  createWorker() {
    const { id, freshSession } = this.session
    this.runtimeReady = false
    const query = new URLSearchParams({ scope: id, fresh: String(freshSession) })
    const separator = this.options.serverWorkerUrl.includes('?') ? '&' : '?'
    this.worker = new this.environment.WorkerClass(
      `${this.options.serverWorkerUrl}${separator}${query}`,
      { type: 'module' },
    )

    this.worker.onmessage = event => {
      if (isControlMessage(event.data, ProtocolMessageType.RUNTIME_LOG)) {
        const { stage, status, message } = event.data.payload
        this.progress(stage, status, message)
        return
      }

      if (isControlMessage(event.data, ProtocolMessageType.RUNTIME_READY)) {
        if (this.runtimeReady) {
          this.emit(PlaygroundEventType.WOKE_UP)
          return
        }
        this.runtimeReady = true
        this.installedApps = Array.isArray(event.data.payload?.installedApps)
          ? [...event.data.payload.installedApps]
          : []
        this.progress(RuntimeStage.FRAPPE, 'done', 'Frappe booted successfully!')
        this.readyTimer = this.environment.setTimeoutFn(() => {
          if (!this.disposed) {
            this.emit(PlaygroundEventType.READY, { instanceId: this.session.id })
          }
        }, this.options.readyDelayMs)
        return
      }

      if (isControlMessage(event.data, ProtocolMessageType.RUNTIME_ERROR)) {
        this.emitError(new Error(event.data.payload.message))
        return
      }

      if (isControlMessage(event.data, ProtocolMessageType.APP_INSTALL_RESULT)) {
        const pending = this.pendingAppOperations.get(event.data.payload.requestId)
        if (!pending) return
        this.environment.clearTimeoutFn(pending.timeout)
        this.pendingAppOperations.delete(event.data.payload.requestId)
        if (event.data.payload.installed) {
          if (!this.installedApps.includes(event.data.payload.appId)) {
            this.installedApps.push(event.data.payload.appId)
          }
          pending.resolve(event.data.payload)
        }
        else pending.reject(new Error(event.data.payload.error || 'App installation failed.'))
        return
      }

      if (isControlMessage(event.data, ProtocolMessageType.APP_UNINSTALL_RESULT)) {
        const pending = this.pendingAppOperations.get(event.data.payload.requestId)
        if (!pending) return
        this.environment.clearTimeoutFn(pending.timeout)
        this.pendingAppOperations.delete(event.data.payload.requestId)
        if (event.data.payload.uninstalled) {
          this.installedApps = this.installedApps.filter(appId => appId !== event.data.payload.appId)
          pending.resolve(event.data.payload)
        } else {
          pending.reject(new Error(event.data.payload.error || 'App uninstall failed.'))
        }
      }
    }

    this.worker.onerror = error => {
      let msg = error.message || 'Frappe runtime failed to start.'
      if (msg.includes("Unexpected token '{'") || msg.includes('SyntaxError') || msg.includes('import declarations')) {
        msg = 'Your browser is too old to run the Frappe Playground. Please update your device or switch to a modern browser.'
      }
      this.emitError(new Error(msg))
    }
  }

  setupChannel() {
    if (this.disposed || !this.worker) return

    const sendInit = serviceWorker => {
      if (!serviceWorker || this.disposed) return
      const channel = new this.environment.MessageChannelClass()
      serviceWorker.postMessage(
        createInitChannelMessage(this.session.id, {
          freshSession: this.session.freshSession,
        }),
        [channel.port1],
      )
      this.worker.postMessage(
        createInitChannelMessage(this.session.id, {
          freshSession: this.session.freshSession,
        }),
        [channel.port2],
      )
    }

    const serviceWorker = this.environment.navigator.serviceWorker
    const target = this.expectedServiceWorker(serviceWorker, this.registration)
      || this.serviceWorkerTarget
    if (target) {
      this.serviceWorkerTarget = target
      sendInit(target)
    } else {
      serviceWorker.ready.then(registration => {
        this.serviceWorkerTarget = registration.active
        sendInit(registration.active)
      })
    }
  }

  setupRecovery() {
    this.recoveryChannel = new this.environment.BroadcastChannelClass(
      this.options.recoveryChannelName,
    )
    this.recoveryChannel.onmessage = event => {
      if (isControlMessage(event.data, ProtocolMessageType.RECOVERY_REQUEST)) {
        console.log('[Playground] Service Worker requested channel recovery.')
        this.emit(PlaygroundEventType.WAKING_UP)
        this.setupChannel()
      }
    }

    this.handleVisibilityChange = () => {
      if (this.environment.document.visibilityState === 'visible') {
        this.checkForServiceWorkerUpdate()
        this.setupChannel()
      }
    }
    this.environment.document.addEventListener('visibilitychange', this.handleVisibilityChange)
  }

  checkForServiceWorkerUpdate() {
    return this.swCoordinator.checkForServiceWorkerUpdate(this.registration)
  }

  withRegistrationTimeout(registrationPromise) {
    return this.swCoordinator.withRegistrationTimeout(
      registrationPromise,
      this.abortController?.signal,
    )
  }

  waitUntilReady() {
    if (this.runtimeReady) return Promise.resolve()
    const signal = this.abortController?.signal
    return new Promise((resolve, reject) => {
      const finish = (callback, value) => {
        offReady()
        offError()
        signal?.removeEventListener('abort', abort)
        callback(value)
      }
      const offReady = this.on(PlaygroundEventType.READY, () => finish(resolve))
      const offError = this.on(PlaygroundEventType.ERROR, ({ error }) => finish(reject, error))
      const abort = () => finish(reject, signal.reason)
      signal?.addEventListener('abort', abort, { once: true })
      if (signal?.aborted) abort()
    })
  }

  isExpectedServiceWorker(worker) {
    return this.swCoordinator.isExpectedServiceWorker(worker)
  }

  expectedServiceWorker(serviceWorker, registration) {
    return this.swCoordinator.expectedServiceWorker(serviceWorker, registration)
  }

  waitForExpectedServiceWorker(serviceWorker) {
    return this.swCoordinator.waitForExpectedServiceWorker(
      serviceWorker,
      this.abortController?.signal,
    )
  }

  emitError(error) {
    this.emit(PlaygroundEventType.ERROR, {
      error,
      message: error.message || 'Frappe runtime failed to start.',
    })
  }

  installApp(appId) {
    validateAppId(appId)
    return this.requestAppOperation(appId, createAppInstallMessage, 'installing')
  }

  uninstallApp(appId) {
    validateAppId(appId)
    if (!this.installedApps.includes(appId)) {
      return Promise.reject(new Error(`${appId} is not installed in this playground.`))
    }
    return this.requestAppOperation(appId, createAppUninstallMessage, 'uninstalling')
  }

  requestAppOperation(appId, createMessage, action) {
    if (!this.runtimeReady || !this.worker) {
      return Promise.reject(new Error(`The playground must finish booting before ${action} apps.`))
    }

    const requestId = this.options.cryptoApi?.randomUUID?.()
      || `${Date.now()}-${Math.random().toString(16).slice(2)}`
    return new Promise((resolve, reject) => {
      const timeout = this.environment.setTimeoutFn(() => {
        this.pendingAppOperations.delete(requestId)
        reject(new Error(`Timed out while ${action} ${appId}.`))
      }, this.options.appOperationTimeoutMs)
      this.pendingAppOperations.set(requestId, { resolve, reject, timeout })
      try {
        this.worker.postMessage(createMessage(requestId, appId))
      } catch (error) {
        this.environment.clearTimeoutFn(timeout)
        this.pendingAppOperations.delete(requestId)
        reject(error)
      }
    })
  }

  listInstalledApps() {
    return [...this.installedApps]
  }

  dispose() {
    this.disposed = true
    this.abortController?.abort()
    this.startPromise = null
    this.environment.clearTimeoutFn(this.readyTimer)
    this.runtimeReady = false
    this.installedApps = []
    for (const pending of this.pendingAppOperations.values()) {
      this.environment.clearTimeoutFn(pending.timeout)
      pending.reject(new Error('Playground stopped before the app operation completed.'))
    }
    this.pendingAppOperations.clear()
    if (this.session) {
      try {
        this.serviceWorkerTarget?.postMessage(createCloseChannelMessage(this.session.id))
      } catch (_) {
        // The service worker may already have been replaced.
      }
    }
    this.worker?.terminate()
    this.worker = null
    this.registration = null
    this.serviceWorkerTarget = null
    this.recoveryChannel?.close()
    this.recoveryChannel = null

    if (this.handleControllerChange) {
      if (typeof this.handleControllerChange === 'function') {
        this.handleControllerChange()
      } else {
        const serviceWorker = this.environment.navigator?.serviceWorker
        serviceWorker?.removeEventListener('controllerchange', this.handleControllerChange)
      }
      this.handleControllerChange = null
    }
    if (this.handleVisibilityChange) {
      this.environment.document?.removeEventListener(
        'visibilitychange',
        this.handleVisibilityChange,
      )
    }
    this.listeners.clear()
  }
}

export function createPlayground(options) {
  return new PlaygroundController(options)
}
